import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import CustomHelmet from 'components/CustomHelmet';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { DEFAULT_THEME_COLOR } from 'appenv';
import { UserSessionContext } from 'components/UserSession';
import { SearchFieldStateProvider } from 'components/SearchFieldContext';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';

import { selectLocale } from 'models/localization';
import { selectLocalizedEventName } from 'models/virtual_event';

import { useMediaQuery } from '@material-ui/core';
import { TabsContextStateProvider, TabsContext } from 'components/TabsContext';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import Routes from 'Routes';
import SearchResultLoader from './SearchResultLoader';

const RootContainer = styled.div`
  height: 100%;
  overflow: hidden;
  background-color: #F0F2F5;
  display: flex;
  flex-direction: column;
`;

const StyledTabsContainer = withStyles(() => ({
  root: {
    flex: '0 0 auto',
    marginBottom: '6px',
    paddingTop: '16px',
  },
}))(Paper);

const StyledTabs = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const Custom = withStyles(() => ({
    indicator: {
      backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      height: '4px',
    },
  }))(Tabs);
  return <Custom {...props}>{children}</Custom>;
};
StyledTabs.propTypes = {
  children: PropTypes.node,
};
StyledTabs.defaultProps = {
  children: null,
};

const StyledTab = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const Custom = withStyles(() => ({
    root: {
      color: '#A2AAB3',
      fontSize: '18px',
      fontWeight: 'bold',
      textTransform: 'none',
    },
    selected: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
    wrapper: {
      display: 'block',
    },
  }))(Tab);
  return <Custom {...props}>{children}</Custom>;
};
StyledTab.propTypes = {
  children: PropTypes.node,
};
StyledTab.defaultProps = {
  children: null,
};

const PageContentContainer = styled.div`
  flex: 1 1 auto;
  height: auto;
  width: 100%;
  overflow: auto;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  @media only screen and (max-width: 960px) {
    margin: 0 5vw;
  }
`;

const SearchResultTabs = ({ viewableTabs }) => {
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const { activeTabKey, setActiveTabKey } = useContext(TabsContext);

  const handleTabChange = useCallback((_, selectedTab) => {
    setActiveTabKey(selectedTab);
  }, [setActiveTabKey]);

  return (
    <StyledTabsContainer elevation={3}>
      <StyledTabs
        centered
        variant={isDesktop ? 'standard' : 'scrollable'}
        value={activeTabKey}
        scrollButtons="auto"
        onChange={handleTabChange}
      >
        {viewableTabs}
      </StyledTabs>
    </StyledTabsContainer>
  );
};

SearchResultTabs.propTypes = {
  viewableTabs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const SearchResultPageContainer = () => {
  const { t } = useTranslation();
  const [scrollableTarget, setScrollableTarget] = useState();
  const locale = useSelector(selectLocale);
  const {
    pageNames: {
      all,
      search,
      exhibitors,
      webinar,
      speakers,
      participant,
      products,
    },
    features,
  } = useSelector((state) => state.eventConfigs);
  const eventName = useSelector(selectLocalizedEventName);

  const { canSee } = useContext(UserSessionContext);

  const pageWrapperRef = useRef();

  useEffect(() => {
    setScrollableTarget(pageWrapperRef.current);
  }, []);

  const pageTitle = search?.[locale] || t('page.search', 'Search Results');

  const tabList = [
    {
      label: all?.[locale] || t('all', 'All'),
      type: 'all',
      condition: true,
    },
    {
      label: exhibitors?.[locale] || t('page.exhibitors', 'Exhibitors'),
      type: 'exhibitor',
      condition: canSee('hall'),
    },
    {
      label: webinar?.[locale] || t('page.webinar', 'Webinars'),
      type: 'webinar',
      condition: canSee('webinar'),
    },
    {
      label: speakers?.[locale] || t('page.speakers', 'Speakers'),
      type: 'speakers',
      condition: canSee('webinar'),
    },
    {
      label: participant?.[locale] || t('page.participants', 'Participants'),
      type: 'participant',
      condition: canSee('participant'),
    },
    {
      label: products?.[locale] || t('page.products', 'Participants'),
      type: 'products',
      condition: features.includes('productPage'),
    },
  ];
  const viewableTabs = tabList
    .filter((it) => it.condition)
    .map(({ label }) => <StyledTab key={label} label={label} />);

  const viewableTabTypeList = tabList
    .filter((it) => it.condition)
    .map(({ type }) => type);
  return (
    <TabsContextStateProvider>
      <RootContainer>
        <CustomHelmet>
          <meta charSet="utf-8" />
          <title>{`${pageTitle} | ${eventName}`}</title>
          <meta name="description" content={`Search results for booths in ${eventName}.`} />
        </CustomHelmet>
        {viewableTabs.length > 2 && (
          <SearchResultTabs viewableTabs={viewableTabs} />
        )}
        <ScrollableContainerWrapper
          WrapperContainer={PageContentContainer}
          refOverride={pageWrapperRef}
        >
          <SearchFieldStateProvider>
            <SearchResultLoader
              scrollableTarget={scrollableTarget}
              viewableTabTypeList={viewableTabTypeList}
            />
          </SearchFieldStateProvider>
          <FloatingFooter hashState={Routes.search} />
        </ScrollableContainerWrapper>
      </RootContainer>
    </TabsContextStateProvider>
  );
};

export default SearchResultPageContainer;
